import {RSubscribeToConversationTask} from '../../../../srcCommon/tasks/conversations/subscribeToConversationTask';
import {createUpdateConversationsTrackerStatusTask} from '../../../../srcCommon/tasks/conversations/updateConversationsTrackerStatusTask';
import {createTask} from '../../../../srcCommon/tasks/task';
import {
  buildConversationUpdater,
  bumpConversationTracker,
  findTracker,
} from '../../../helpers/models/conversationHelpers';
import {BumpedAtTimestamp, ConversationId} from '../../../models/api/conversationCommonTypes';
import {
  ConversationTrackerStagesEnum,
  ConversationTrackerStatusesEnum,
} from '../../../models/api/conversationTrackerModel';
import {StateGetter} from '../../appStore';
import {ThunkAsyncAction} from '../../appThunk';
import {ConversationUndoBannerTypesEnum} from '../../panels/app/banners/bannersState';
import {CellsContext} from '../../panels/cells/context/cellsContext';
import {updateConversationsAction} from './updateConversationsActions';

/*
 * Subscribe a.k.a. follow a.k.a. unmute.
 */

interface SubscribeToConversationsOptions {
  previousStatus?: ConversationTrackerStatusesEnum;
  // TODO: Make it a function ala findConversationActionContext.
  previousBumpedAt?: Record<ConversationId, BumpedAtTimestamp | undefined>;
}

export function subscribeToConversationsAction(
  cellsContext: CellsContext,
  conversationIds: ReadonlyArray<number>,
  options: SubscribeToConversationsOptions = {},
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const {teammateId} = cellsContext;

    const offlineTask = createSubscribeOfflineTask(getState, cellsContext, conversationIds, options);

    await dispatch(
      updateConversationsAction(
        conversationIds,
        (id) => makeSubscribePatch(teammateId, options.previousStatus, options.previousBumpedAt?.[id]),
        (conversation) => {
          const tracker = findTracker(conversation, teammateId);

          // If we already have a tracker, keep its status. Otherwise, default to INBOX.
          const status =
            tracker.stage !== ConversationTrackerStagesEnum.NONE
              ? tracker.status
              : ConversationTrackerStatusesEnum.INBOX;
          const newStatus = options.previousStatus || status;

          // Undo the bump, if necessary.
          const previousBumpedAt = options.previousBumpedAt && options.previousBumpedAt[conversation.id];
          const newBumpedAt = previousBumpedAt || tracker.bumpedAt;

          const newTracker = {
            ...tracker,
            stage: ConversationTrackerStagesEnum.SUBSCRIBER,
            status: newStatus,
            bumpedAt: newBumpedAt,
          };
          const newTrackers = {...conversation.trackers, [teammateId]: newTracker};
          return {...conversation, trackers: newTrackers};
        },
        {cellsContext, offlineTask},
      ),
    );
  };
}

function makeSubscribePatch(
  teammateId: number,
  status: ConversationTrackerStatusesEnum | undefined,
  bumpedAt: BumpedAtTimestamp | undefined,
) {
  return {
    addTrackers: [
      {
        teammateId,
        stage: ConversationTrackerStagesEnum.SUBSCRIBER,
        // If the tracker already exists, the backend will keep its status. Otherwise, it defaults to INBOX.
        status,
        bump: false,
        // Undo the bump, if necessary.
        bumpedAt,
      },
    ],
  };
}

function createSubscribeOfflineTask(
  getState: StateGetter,
  cellsContext: CellsContext,
  conversationIds: ReadonlyArray<number>,
  options: SubscribeToConversationsOptions,
) {
  // If we are undoing a mute (+ archive and bump), then use the offline task with conflict resolution.
  const {previousStatus, previousBumpedAt} = options;
  if (previousStatus || previousBumpedAt) {
    return createUpdateConversationsTrackerStatusTask(getState, cellsContext, conversationIds, {
      shouldAllowAssigneeContext: true,
      shouldAllowTeamContext: true,
      status: previousStatus,
      stage: ConversationTrackerStagesEnum.SUBSCRIBER,
      findBumpedAt: (conversationId) => previousBumpedAt && previousBumpedAt[conversationId],
    });
  }

  // If we are doing a simple subscribe, use the offline task without conflict resolution.
  return createTask(RSubscribeToConversationTask, {
    conversations: conversationIds.map((id) => ({id})),
    teammateId: cellsContext.teammateId,
    initiatedAt: Date.now(),
  });
}

/*
 * Unsubscribe a.k.a. unfollow a.k.a. mute.
 */

export function unsubscribeFromConversationsAction(
  cellsContext: CellsContext,
  conversationIds: ReadonlyArray<number>,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const {teammateId} = cellsContext;

    const offlineTask = createUnsubscribeOfflineTask(getState, cellsContext, conversationIds);

    const patch = {
      addTrackers: [
        {
          teammateId,
          stage: ConversationTrackerStagesEnum.TRACKER,
          status: ConversationTrackerStatusesEnum.ARCHIVED,
          // Bump the tracker, consistent with tracker archiving.
          bump: true,
        },
      ],
    };

    const options = {
      cellsContext,
      offlineTask,
      undo: {
        type: ConversationUndoBannerTypesEnum.STAGE,
      },
    };

    await dispatch(
      updateConversationsAction(
        conversationIds,
        patch,
        buildConversationUpdater(
          // Bump the tracker.
          bumpConversationTracker(teammateId),
          // Set the tracker stage and status.
          (conversation) => {
            const tracker = findTracker(conversation, teammateId);
            const newTracker = {
              ...tracker,
              stage: ConversationTrackerStagesEnum.TRACKER,
              status: ConversationTrackerStatusesEnum.ARCHIVED,
            };
            const newTrackers = {...conversation.trackers, [teammateId]: newTracker};
            return {...conversation, trackers: newTrackers};
          },
        ),
        options,
      ),
    );
  };
}

export function unsubscribeVisitorFromConversationsAction(
  cellsContext: CellsContext,
  conversationIds: ReadonlyArray<number>,
): ThunkAsyncAction {
  return async (dispatch) => {
    const {teammateId} = cellsContext;

    const patch = {
      addTrackers: [
        {
          teammateId,
          stage: ConversationTrackerStagesEnum.TRACKER,
          bump: false,
        },
      ],
    };

    const options = {
      cellsContext,
      undo: {
        type: ConversationUndoBannerTypesEnum.STAGE,
      },
    };

    await dispatch(
      updateConversationsAction(
        conversationIds,
        patch,
        buildConversationUpdater(
          // Set the tracker stage.
          (conversation) => {
            const tracker = findTracker(conversation, teammateId);
            const newTracker = {
              ...tracker,
              stage: ConversationTrackerStagesEnum.TRACKER,
            };

            const newTrackers = {...conversation.trackers, [teammateId]: newTracker};
            return {...conversation, trackers: newTrackers};
          },
        ),
        options,
      ),
    );
  };
}

function createUnsubscribeOfflineTask(
  getState: StateGetter,
  cellsContext: CellsContext,
  conversationIds: ReadonlyArray<number>,
) {
  return createUpdateConversationsTrackerStatusTask(getState, cellsContext, conversationIds, {
    shouldAllowAssigneeContext: true,
    shouldAllowTeamContext: true,
    stage: ConversationTrackerStagesEnum.TRACKER,
    status: ConversationTrackerStatusesEnum.ARCHIVED,
  });
}
